import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLinkIcon, CheckIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { ReviewCard } from '../components/ReviewCard';
import { Button } from '../components/Button';
import { exchanges } from '../data/mockData';
export function ExchangeDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const exchange = exchanges.find(e => e.id === id);
  if (!exchange) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-380">
        <div className="text-center" data-id="element-381">
          <h1 className="text-2xl font-bold mb-4" data-id="element-382">Exchange not found</h1>
          <Link to="/exchanges" data-id="element-383">
            <Button data-id="element-384">Back to Exchanges</Button>
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50" data-id="element-385">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-12" data-id="element-386">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-387">
          <Link to="/exchanges" className="inline-flex items-center gap-2 text-slate-300 hover:text-white mb-6 transition-colors" data-id="element-388">
            <ArrowLeftIcon size={20} data-id="element-389" />
            <span data-id="element-390">Back to Exchanges</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-id="element-391">
            <div className="flex items-center gap-4" data-id="element-392">
              <img src={exchange.logo} alt={exchange.name} className="w-16 h-16 rounded-xl bg-white/10 p-2" data-id="element-393" />
              <div data-id="element-394">
                <h1 className="text-4xl font-bold mb-2" data-id="element-395">{exchange.name}</h1>
                <TrustScore score={exchange.trustScore} size="md" data-id="element-396" />
              </div>
            </div>

            <a href={exchange.website} target="_blank" rel="noopener noreferrer" data-id="element-397">
              <Button size="lg" className="flex items-center gap-2" data-id="element-398">
                <span data-id="element-399">Visit Exchange</span>
                <ExternalLinkIcon size={18} data-id="element-400" />
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-401">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-402">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8" data-id="element-403">
            {/* Key Metrics */}
            <Card className="p-6" data-id="element-404">
              <h2 className="text-2xl font-bold mb-6" data-id="element-405">Key Metrics</h2>
              <div className="grid grid-cols-2 gap-6" data-id="element-406">
                <div data-id="element-407">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-408">
                    24h Trading Volume
                  </div>
                  <div className="text-xl font-semibold" data-id="element-409">
                    ${(exchange.tradingVolume / 1000000000).toFixed(2)}B
                  </div>
                </div>
                <div data-id="element-410">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-411">
                    Supported Coins
                  </div>
                  <div className="text-xl font-semibold" data-id="element-412">
                    {exchange.supportedCoins}
                  </div>
                </div>
                <div data-id="element-413">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-414">Trading Fee</div>
                  <div className="text-xl font-semibold" data-id="element-415">
                    {exchange.tradingFee}
                  </div>
                </div>
                <div data-id="element-416">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-417">
                    Withdrawal Fee
                  </div>
                  <div className="text-xl font-semibold" data-id="element-418">
                    {exchange.withdrawalFee}
                  </div>
                </div>
              </div>
            </Card>

            {/* About */}
            <Card className="p-6" data-id="element-419">
              <h2 className="text-2xl font-bold mb-4" data-id="element-420">About {exchange.name}</h2>
              <p className="text-slate-700 leading-relaxed mb-6" data-id="element-421">
                {exchange.description}
              </p>
            </Card>

            {/* Features */}
            <Card className="p-6" data-id="element-422">
              <h2 className="text-2xl font-bold mb-4" data-id="element-423">Features</h2>
              <div className="flex flex-wrap gap-2" data-id="element-424">
                {exchange.features.map((feature, index) => <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium" data-id="element-425">
                    {feature}
                  </span>)}
              </div>
            </Card>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-426">
              <Card className="p-6" data-id="element-427">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-428">
                  <CheckIcon className="text-green-600" size={24} data-id="element-429" />
                  <span data-id="element-430">Pros</span>
                </h3>
                <ul className="space-y-2" data-id="element-431">
                  {exchange.pros.map((pro, index) => <li key={index} className="flex items-start gap-2" data-id="element-432">
                      <CheckIcon className="text-green-600 flex-shrink-0 mt-0.5" size={16} data-id="element-433" />
                      <span className="text-slate-700" data-id="element-434">{pro}</span>
                    </li>)}
                </ul>
              </Card>

              <Card className="p-6" data-id="element-435">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" data-id="element-436">
                  <XIcon className="text-red-600" size={24} data-id="element-437" />
                  <span data-id="element-438">Cons</span>
                </h3>
                <ul className="space-y-2" data-id="element-439">
                  {exchange.cons.map((con, index) => <li key={index} className="flex items-start gap-2" data-id="element-440">
                      <XIcon className="text-red-600 flex-shrink-0 mt-0.5" size={16} data-id="element-441" />
                      <span className="text-slate-700" data-id="element-442">{con}</span>
                    </li>)}
                </ul>
              </Card>
            </div>

            {/* Reviews */}
            <div data-id="element-443">
              <h2 className="text-2xl font-bold mb-6" data-id="element-444">User Reviews</h2>
              <div className="space-y-4" data-id="element-445">
                {exchange.reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-446" />)}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-447">
            <Card className="p-6" data-id="element-448">
              <h3 className="font-bold text-lg mb-4" data-id="element-449">Quick Stats</h3>
              <div className="space-y-4" data-id="element-450">
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-451">
                  <span className="text-slate-600" data-id="element-452">Rank</span>
                  <span className="font-semibold" data-id="element-453">#{exchange.rank}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-454">
                  <span className="text-slate-600" data-id="element-455">Trading Fee</span>
                  <span className="font-semibold" data-id="element-456">{exchange.tradingFee}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-457">
                  <span className="text-slate-600" data-id="element-458">Coins Listed</span>
                  <span className="font-semibold" data-id="element-459">
                    {exchange.supportedCoins}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-460">
                  <span className="text-slate-600" data-id="element-461">Total Reviews</span>
                  <span className="font-semibold" data-id="element-462">
                    {exchange.reviews.length}
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200" data-id="element-463">
              <h3 className="font-bold text-lg mb-2" data-id="element-464">Trust Score Breakdown</h3>
              <div className="text-3xl font-bold text-blue-600 mb-4" data-id="element-465">
                {exchange.trustScore.toFixed(1)}/10
              </div>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-466">
                Our trust score evaluates security measures, regulatory
                compliance, user experience, and customer support quality.
              </p>
            </Card>

            <Card className="p-6" data-id="element-467">
              <h3 className="font-bold text-lg mb-4" data-id="element-468">Official Links</h3>
              <a href={exchange.website} target="_blank" rel="noopener noreferrer" className="block" data-id="element-469">
                <Button variant="outline" className="w-full flex items-center justify-center gap-2" data-id="element-470">
                  <span data-id="element-471">Visit Website</span>
                  <ExternalLinkIcon size={16} data-id="element-472" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}